import { mockResults } from '../data/mock_results';
import type {
  AthleteProfile,
  LeaderboardQuery,
  LeaderboardRow,
  PublicResult,
  ResultRepository,
} from '../types/result';
import {
  buildAverages,
  buildComparisons,
  findParticipantById,
  getDisplayName,
  searchLeaderboard,
  sortResultsByOverallScore,
  toLeaderboardRows,
} from './result_utils';

interface MockRepositoryOptions {
  latencyMs?: number;
  failureRate?: number;
  results?: PublicResult[];
}

export class MockResultRepository implements ResultRepository {
  private readonly latencyMs: number;
  private readonly failureRate: number;
  private readonly results: PublicResult[];

  public constructor(options: MockRepositoryOptions = {}) {
    this.latencyMs = options.latencyMs ?? 80;
    this.failureRate = options.failureRate ?? 0;
    this.results = options.results ?? mockResults;
  }

  private async wait(): Promise<void> {
    await new Promise((resolve) => window.setTimeout(resolve, this.latencyMs));
    if (Math.random() < this.failureRate) {
      throw new Error('The mock results service is temporarily unavailable.');
    }
  }

  public async findByExternalId(externalId: string): Promise<PublicResult | null> {
    await this.wait();
    return findParticipantById(this.results, externalId);
  }

  public async getAthleteProfile(externalId: string): Promise<AthleteProfile | null> {
    await this.wait();
    const athlete = findParticipantById(this.results, externalId);
    if (!athlete) {
      return null;
    }

    const participants = this.results.filter((result) => !result.isProfessional);
    const professionals = this.results.filter((result) => result.isProfessional);
    const ranked = toLeaderboardRows(this.results, true);
    const row = ranked.find((candidate) => candidate.externalId === athlete.externalId);
    const sprintMilestones = [
      { distanceYards: 10, key: 'sprint10Seconds' },
      { distanceYards: 20, key: 'sprint20Seconds' },
      { distanceYards: 30, key: 'sprint30Seconds' },
      { distanceYards: 40, key: 'sprint40Seconds' },
    ] as const;

    if (!row || row.rank === null) {
      return null;
    }

    return {
      athlete,
      rank: row.rank,
      participantCount: participants.length,
      participantAverages: buildAverages(participants),
      professionalBenchmarks: sortResultsByOverallScore(professionals).slice(0, 2),
      comparisons: buildComparisons(athlete, professionals),
      sprintChart: {
        athlete: sprintMilestones.flatMap(({ distanceYards, key }) => {
          const timeSeconds = athlete[key];
          return timeSeconds === null ? [] : [{ distanceYards, timeSeconds }];
        }),
        participantAverage: sprintMilestones.flatMap(({ distanceYards, key }) => {
          const times = participants.flatMap((result) => {
            const time = result[key];
            return time === null ? [] : [time];
          });
          return times.length === 0
            ? []
            : [{
                distanceYards,
                timeSeconds: times.reduce((total, time) => total + time, 0) / times.length,
              }];
        }),
      },
      broadJumpScatterPoints: participants.flatMap((result) => {
        if (result.heightInches === null || result.broadJumpInches === null) {
          return [];
        }

        return [{
          athleteId: result.externalId,
          athleteName: getDisplayName(result),
          heightInches: result.heightInches,
          broadJumpInches: result.broadJumpInches,
          isCurrentAthlete: result.externalId === athlete.externalId,
        }];
      }),
    };
  }

  public async listLeaderboard(query: LeaderboardQuery = {}): Promise<LeaderboardRow[]> {
    await this.wait();
    const rows = toLeaderboardRows(this.results, true);
    return searchLeaderboard(rows, query.search ?? '');
  }
}

export const resultRepository: ResultRepository = new MockResultRepository();
